// Client-side whitelist gate: every model function call is validated here before anything happens.
// It can navigate to an allowlisted route, run read-only checks, read user status or keep a note they asked for.

import { ASSISTANT_DIAGNOSTIC_KINDS, AssistantDiagnosticKind } from '../../../../../../pure-js/types/AssistantKnowledge'
import {
  ASSISTANT_TOOL_NAME_LIST,
  AssistantToolErrorCode,
  AssistantToolName,
  type AssistantUserStatus
} from '../../../../../../pure-js/types/AppAssistant'
import { resolveAssistantRouteUrl } from '../../../../../../pure-js/libs/assistantNavigation'
import type { UserAiMemoryKind } from '../../../../../../pure-js/types/CoachTypes'
import { isUserAiMemoryKind } from '../../../../../../pure-js/libs/AICoachStateHelper'
import {
  SILENT_ASSISTANT_TOOL_RESPONSE,
  type AssistantToolCall,
  type AssistantToolResult
} from './AssistantRealtimeTransport'
import { checkCamera, checkConnection, checkMicrophone } from './assistantDiagnostics'

export type WherebyRoomState = { inRoom: boolean; roomName?: string; expectedRoomName?: string }

// No audience here on purpose: it is resolved server-side and locked into the system instruction.
export type AssistantDispatcherDeps = {
  /** Navigate to an already-validated in-app URL. */
  navigate: (url: string) => void
  /** Read-only status fetch (authenticated callable). */
  getUserStatus: () => Promise<AssistantUserStatus | undefined>
  /**
   * Route a question through the typed assistant callable and return its answer as text, so the spoken
   * path answers exactly what a typed question would.
   */
  answerFromUserData?: (question: string) => Promise<string>
  /** Current Whereby room state, if the app knows it. */
  getWherebyRoomState?: () => WherebyRoomState
  /** Surface a "refresh page" affordance to the user (never auto-reloads). */
  requestRefresh?: () => void
  /** Show the "talk to a person" card. Offering it is all this does; sending stays the user's click. */
  offerHumanSupport?: () => void
  /** Force a re-fetch of the user's upcoming activities (used when a game seems to be missing). */
  refetchUpcomingActivities?: () => void
  /** Wind the call down. The caller waits for her closing words before the session actually closes. */
  endConversation?: () => void
  /** Keep what they asked her to remember, or how they want to be coached, the moment they say it. */
  rememberForLater?: (note: string, kind: UserAiMemoryKind) => Promise<boolean>
}

export type AssistantToolDispatcher = (call: AssistantToolCall) => Promise<AssistantToolResult>

const isAllowedTool = (name: string): name is AssistantToolName =>
  ASSISTANT_TOOL_NAME_LIST.includes(name as AssistantToolName)

const isAllowedDiagnostic = (kind: string): kind is AssistantDiagnosticKind =>
  ASSISTANT_DIAGNOSTIC_KINDS.includes(kind as AssistantDiagnosticKind)

export function createAssistantToolDispatcher(deps: AssistantDispatcherDeps): AssistantToolDispatcher {
  return async (call: AssistantToolCall): Promise<AssistantToolResult> => {
    if (!isAllowedTool(call.name)) {
      return { error: AssistantToolErrorCode.TOOL_NOT_ALLOWED, tool: call.name }
    }

    switch (call.name) {
      case AssistantToolName.NAVIGATE:
        return dispatchNavigate(deps, call.args)
      case AssistantToolName.RUN_DIAGNOSTIC:
        return dispatchDiagnostic(deps, call.args)
      case AssistantToolName.GET_USER_STATUS:
        return dispatchGetUserStatus(deps)
      case AssistantToolName.ANSWER_FROM_MY_DATA:
        return dispatchAnswerFromUserData(deps, call.args)
      case AssistantToolName.OFFER_HUMAN_SUPPORT:
        deps.offerHumanSupport?.()
        return { offered: true }
      case AssistantToolName.END_CONVERSATION:
        // The transport acknowledges this without scheduling another utterance after her goodbye.
        deps.endConversation?.()
        return SILENT_ASSISTANT_TOOL_RESPONSE
      case AssistantToolName.REMEMBER_FOR_LATER:
        return dispatchRememberForLater(deps, call.args)
      default:
        return { error: AssistantToolErrorCode.TOOL_NOT_ALLOWED, tool: call.name }
    }
  }
}

/** Keep a note they asked for. Shared with the coach, so one check governs both. */
export async function dispatchRememberForLater(
  deps: Pick<AssistantDispatcherDeps, 'rememberForLater'>,
  args: Record<string, unknown>
): Promise<AssistantToolResult> {
  if (!deps.rememberForLater) return { error: AssistantToolErrorCode.TOOL_NOT_AVAILABLE }

  const note = typeof args.note === 'string' ? args.note.trim() : ''
  const { kind } = args
  const isNoteUsable = note.length > 0 && isUserAiMemoryKind(kind)

  if (!isNoteUsable) {
    return { error: AssistantToolErrorCode.TOOL_FAILED, tool: AssistantToolName.REMEMBER_FOR_LATER }
  }

  const isRemembered = await deps.rememberForLater(note, kind).catch(() => false)
  if (isRemembered) return { remembered: true }

  // She hears it was not kept, and the one way to fix it that is hers to take.
  return {
    error: AssistantToolErrorCode.TOOL_FAILED,
    tool: AssistantToolName.REMEMBER_FOR_LATER,
    reason: 'Not saved. If it named anyone, save it again without the name; otherwise tell them it could not be saved.'
  }
}

/** Resolve a `navigate` call against the allowlist and go. Shared with the coach, so one check governs both. */
export function dispatchNavigate(
  deps: Pick<AssistantDispatcherDeps, 'navigate'>,
  args: Record<string, unknown>
): AssistantToolResult {
  const routeKey = typeof args.routeKey === 'string' ? args.routeKey : ''
  const url = resolveAssistantRouteUrl(routeKey)
  if (!url) return { error: AssistantToolErrorCode.ROUTE_NOT_ALLOWED, routeKey }
  deps.navigate(url)
  return { navigated: true, routeKey, url }
}

async function dispatchDiagnostic(
  deps: AssistantDispatcherDeps,
  args: Record<string, unknown>
): Promise<AssistantToolResult> {
  const kind = typeof args.kind === 'string' ? args.kind : ''
  if (!isAllowedDiagnostic(kind)) return { error: AssistantToolErrorCode.DIAGNOSTIC_NOT_ALLOWED, kind }

  switch (kind) {
    case AssistantDiagnosticKind.REFRESH_PAGE:
      deps.requestRefresh?.()
      return { kind, recommendation: 'refresh', note: 'Offered a refresh action to the user.' }
    case AssistantDiagnosticKind.CHECK_CONNECTION:
      return { kind, ...(await checkConnection()) }
    case AssistantDiagnosticKind.CHECK_MICROPHONE:
      return { kind, ...(await checkMicrophone()) }
    case AssistantDiagnosticKind.CHECK_CAMERA:
      return { kind, ...(await checkCamera()) }
    case AssistantDiagnosticKind.CHECK_WHEREBY_ROOM:
      return dispatchWherebyRoom(deps, kind)
    case AssistantDiagnosticKind.CHECK_ANALYSIS_STATUS: {
      const status = await deps.getUserStatus()
      return { kind, analysisInProgress: status?.latestSession?.analysisInProgress ?? false }
    }
    case AssistantDiagnosticKind.CHECK_FEEDBACK_READY: {
      const status = await deps.getUserStatus()
      return { kind, feedbackReady: status?.latestSession?.feedbackReady ?? false }
    }
    case AssistantDiagnosticKind.CHECK_UPCOMING_ACTIVITY:
      return dispatchUpcomingActivity(deps, kind)
    default:
      return { error: AssistantToolErrorCode.DIAGNOSTIC_NOT_ALLOWED, kind }
  }
}

function dispatchWherebyRoom(deps: AssistantDispatcherDeps, kind: AssistantDiagnosticKind): AssistantToolResult {
  const state = deps.getWherebyRoomState?.() ?? { inRoom: false }
  const roomMismatch = state.inRoom && !!state.expectedRoomName && state.roomName !== state.expectedRoomName
  return { kind, ...state, roomMismatch }
}

async function dispatchUpcomingActivity(
  deps: AssistantDispatcherDeps,
  kind: AssistantDiagnosticKind
): Promise<AssistantToolResult> {
  // Refresh first — the game may already exist and just be stale on screen.
  deps.refetchUpcomingActivities?.()
  const info = (await deps.getUserStatus())?.upcomingActivity
  return {
    kind,
    refreshed: true,
    hasUpcomingInActiveOrg: info?.hasUpcomingInActiveOrg ?? false,
    // Only the org name — never any sprint/team/participant detail.
    otherOrganizationWithUpcoming: info?.otherOrganizationWithUpcoming?.organizationName
  }
}

async function dispatchGetUserStatus(deps: AssistantDispatcherDeps): Promise<AssistantToolResult> {
  const status = await deps.getUserStatus()
  if (!status) return { available: false }
  return {
    available: true,
    role: status.role,
    analysisInProgress: status.latestSession?.analysisInProgress ?? false,
    feedbackReady: status.latestSession?.feedbackReady ?? false,
    latestSprintTitle: status.latestSprint?.title,
    // Their own goals, in their own words. Without them "what do you know about me" had nothing behind
    // it and Aria answered that she did not know the person at all.
    developmentGoals: status.developmentGoals,
    // Stated explicitly so the model stops offering an organization switch to people who have one org.
    belongsToMultipleOrganizations: status.belongsToMultipleOrganizations,
    hasUpcomingInActiveOrg: status.upcomingActivity?.hasUpcomingInActiveOrg,
    otherOrganizationWithUpcoming: status.upcomingActivity?.otherOrganizationWithUpcoming?.organizationName
  }
}

async function dispatchAnswerFromUserData(
  deps: AssistantDispatcherDeps,
  args: Record<string, unknown>
): Promise<AssistantToolResult> {
  if (!deps.answerFromUserData) return { error: AssistantToolErrorCode.TOOL_NOT_AVAILABLE }

  const question = typeof args.question === 'string' ? args.question.trim() : ''
  if (!question) return { error: AssistantToolErrorCode.QUESTION_REQUIRED }

  return { answer: await deps.answerFromUserData(question) }
}
